import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Analytics } from "aws-amplify";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import React, { PropsWithChildren, useState } from "react";
import { IntlProvider } from "react-intl";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "styled-components";

import { IdentityContextProvider } from "@examspark/api";
import { ConfirmableContainer, themes } from "@examspark/common-ui";
import { Toast } from "@examspark/common-ui/dist/components/toast/Toast";
import { RichContentOptionsProvider } from "@examspark/common-ui-rce5";
import { ExamFeatureContext } from "@examspark/question-player";
import { AnyObject, StringObject } from "@examspark/types";

import { useDebugReporterHotkey } from "../hooks/useDebugReporter";
import { intlMessages } from "../intl";
import { AppState } from "../providers/app-context/appContextReducer";
import AppContextProvider, { defaultState } from "../providers/AppContext";
import LTIProvider from "../providers/LTIProvider";
import WindowProvider from "../providers/WindowContext";
import { safeLocalStorage } from "../safeStorage";

import DebugLocation from "./DebugLocation";
import FeatureFlagProvider from "./FeatureFlagProvider";

mixpanel.init(process.env.REACT_APP_MIXPANEL_ID || "", {
  debug: process.env.REACT_APP_USER_BRANCH !== "master",
});

const initialState =
  safeLocalStorage.parseItem<AppState>("bonfire-state") ?? defaultState;

IdentityContextProvider.setContext({
  userId: initialState.user?.id ?? null,
  workspaceId: initialState.workspaceUser?.id ?? null,
  appId: initialState.app?.id ?? process.env.REACT_APP_EXAMSPARK_APP_ID ?? null,
});

Analytics.configure({
  disabled: true,
});

/**
 * Child to the main root of the application. We split it into a separate
 * InnerApp so we can utilize the LTIContext in the event of an LTI-based
 * launch. This component should ideally handle two routes:
 *
 *  - Login `<LoginRoutes />`
 *  - The logged in portion of the app `<PrivateRoutes />`
 */
const InnerApp = (
  props: PropsWithChildren<{
    queryClient: QueryClient;
    intl?: StringObject;
    themeName: "bonfire" | "gw";
  }>,
) => {
  const [language] = useState(safeLocalStorage.getItem("language") ?? "en");
  const flags = useFlags();

  useDebugReporterHotkey();

  return (
    <WindowProvider>
      <ExamFeatureContext.Provider value={flags}>
        <RichContentOptionsProvider
          options={{
            tools: {
              advancedFormatting: true,
              blockFormatting: true,
              math: true,
            },
          }}
        >
          <ThemeProvider theme={themes[props.themeName]}>
            <ConfirmableContainer />
            <Toast />
            <IntlProvider
              messages={props.intl ?? intlMessages[language]}
              locale={"en"}
              defaultLocale="en"
            >
              <DebugLocation />
              {props.children}
            </IntlProvider>
          </ThemeProvider>
        </RichContentOptionsProvider>
      </ExamFeatureContext.Provider>
    </WindowProvider>
  );
};

/**
 * Main root of the application. Provides:
 *  - LTIContext
 *  - AppContext
 */
const AppRoot = (
  props: PropsWithChildren<{
    awsconfig: AnyObject;
    queryClient: QueryClient;
    allowNoAppId?: boolean;
    codebaseName?: string;
    /** Progressively enable react-router v7 relativeSplatPath for our apps */
    relativeSplatPath?: boolean;
    intl?: StringObject;
    themeName?: "bonfire" | "gw";
  }>,
) => {
  const themeName = props.themeName ?? "bonfire";

  return (
    <BrowserRouter
      future={{
        v7_relativeSplatPath: props.relativeSplatPath,
      }}
    >
      <FeatureFlagProvider>
        <QueryClientProvider client={props.queryClient}>
          <AppContextProvider codebaseName={props.codebaseName}>
            <LTIProvider cognitoUserPoolId={props.awsconfig.aws_user_pools_id}>
              {!process.env.REACT_APP_EXAMSPARK_APP_ID &&
              !props.allowNoAppId ? (
                <div>Missing Application ID</div>
              ) : (
                <InnerApp {...props} themeName={themeName}>
                  {props.children}
                </InnerApp>
              )}
            </LTIProvider>
          </AppContextProvider>
        </QueryClientProvider>
      </FeatureFlagProvider>
    </BrowserRouter>
  );
};

export default AppRoot;
